import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StorageMentionPicker, { buildStorageReference } from './StorageMentionPicker';
import TagEditor from '../Widgets/TagEditor';

const appendReference = (references, nextReference) => {
  const current = Array.isArray(references) ? references : [];
  if (current.some((reference) => reference?.path === nextReference.path)) {
    return current;
  }
  return [...current, nextReference];
};

const StorageMentionInput = forwardRef(({
  value = '',
  onChange,
  storageRefs = [],
  onStorageRefsChange,
  disabled = false,
  richText = false,
  caseid,
  tags,
  scope,
  maxReferences,
  labelHelp,
  ...textFieldProps
}, ref) => {
  const { t } = useTranslation();
  const pickerRef = useRef(null);
  const inputRef = useRef(null);
  const mentionRangeRef = useRef(null);
  const editorRef = useRef(null);
  const mentionTriggeredRef = useRef(false);
  const [error, setError] = useState('');
  const generatedId = useId();
  const editorId = textFieldProps.id || generatedId;
  useImperativeHandle(ref, () => ({
    getValue: () => richText ? editorRef.current?.getValue?.() ?? value : inputRef.current?.value ?? value,
    getStorageReferences: () => richText ? editorRef.current?.getStorageReferences?.() || [] : storageRefs,
    getUnresolvedStorageMentions: () => editorRef.current?.getUnresolvedStorageMentions?.() || [],
    hasPendingAttachments: () => editorRef.current?.hasPendingAttachments?.() || false,
    focus: () => richText ? editorRef.current?.focus?.() : inputRef.current?.focus?.(),
  }), [richText, storageRefs, value]);
  useEffect(() => {
    if (richText && textFieldProps.autoFocus && !disabled) editorRef.current?.focus?.();
  }, [disabled, richText, textFieldProps.autoFocus]);

  const openPickerAtCursor = (start, end) => {
    mentionRangeRef.current = { start, end };
    pickerRef.current?.open();
  };

  const handleChange = (event) => {
    const nextValue = event.target.value;
    const cursor = event.target.selectionStart ?? nextValue.length;
    onChange?.(nextValue);
    if (nextValue[cursor - 1] === '@') {
      openPickerAtCursor(cursor - 1, cursor);
    }
  };

  const handleSelect = (reference, options = {}) => {
    if (disabled) return;
    const normalizedReference = buildStorageReference(reference.path);
    const currentReferences = richText
      ? editorRef.current?.getStorageReferences?.() || storageRefs : storageRefs;
    if (maxReferences && currentReferences.length >= maxReferences
      && !currentReferences.some((item) => item.path === normalizedReference.path)) {
      setError(t('storage_mention.limit', { count: maxReferences }));
      return;
    }
    setError('');
    if (richText) {
      editorRef.current?.insertStorageMention?.(normalizedReference, {
        replaceMentionTrigger: options.replaceMentionTrigger ?? mentionTriggeredRef.current,
        preserveText: options.preserveText,
      });
      mentionTriggeredRef.current = false;
      onStorageRefsChange?.((current) => appendReference(current, normalizedReference));
      return;
    }
    const currentValue = String(value || '');
    const range = mentionRangeRef.current;
    const start = range && currentValue.slice(range.start, range.end) === '@'
      ? range.start
      : (inputRef.current?.selectionStart ?? currentValue.length);
    const end = range && currentValue.slice(range.start, range.end) === '@'
      ? range.end
      : start;
    const insertedValue = `@${normalizedReference.label} `;
    const nextValue = `${currentValue.slice(0, start)}${insertedValue}${currentValue.slice(end)}`;
    onChange?.(nextValue);
    onStorageRefsChange?.((currentReferences) => (
      appendReference(currentReferences, normalizedReference)
    ));
    mentionRangeRef.current = null;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const nextCursor = start + insertedValue.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleRemove = (path) => {
    if (disabled) return;
    setError('');
    onStorageRefsChange?.((currentReferences) => (
      (Array.isArray(currentReferences) ? currentReferences : [])
        .filter((reference) => reference?.path !== path)
    ));
  };

  return (
    <>
      {richText ? <Box>
        {textFieldProps.label && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography component="label" htmlFor={editorId} variant="body2" fontWeight={600}>{textFieldProps.label}</Typography>
          {labelHelp}
        </Box>}
        <TagEditor ref={editorRef} id={editorId} caseid={caseid} tags={tags} scope={scope}
          aria-label={textFieldProps['aria-label'] || textFieldProps.label}
          aria-describedby={textFieldProps['aria-describedby']} value={value} onChange={onChange}
          disabled={disabled} placeholder={textFieldProps.placeholder}
          minRows={textFieldProps.minRows} maxRows={textFieldProps.maxRows}
          storageReferences={storageRefs} onStorageMentionRemove={handleRemove}
          onStorageFilesDrop={(files) => pickerRef.current?.attachFiles(files)}
          onStorageMentionTrigger={() => {
            mentionTriggeredRef.current = true;
            pickerRef.current?.open();
          }} />
        {textFieldProps.helperText && <Typography variant="caption" color="text.secondary">
          {textFieldProps.helperText}
        </Typography>}
      </Box> : <TextField
        {...textFieldProps}
        fullWidth
        value={value}
        onChange={handleChange}
        inputRef={inputRef}
        disabled={disabled}
        helperText={textFieldProps.helperText || t('storage_mention.helper')}
      />}
      {error && <Typography variant="caption" color="error" role="alert">{error}</Typography>}
      <StorageMentionPicker
        ref={pickerRef}
        selectedRefs={storageRefs}
        onSelect={handleSelect}
        onRemove={handleRemove}
        disabled={disabled}
        maxReferences={maxReferences}
        compact
        showSelected={!richText}
      />
    </>
  );
});

export default StorageMentionInput;
